import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  categoryMatchesKind,
  SERVICE_ITEM_CATEGORIES,
  SERVICE_ITEM_KINDS,
  type ServiceItemCategory,
  type ServiceItemKind,
} from '@wodi/domain';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useDeferredValue, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { createItem, listItems, priceListsQuery } from '../api/finance.ts';
import { can, sessionQuery } from '../api/session.ts';
import { CATEGORY_LABELS, KIND_LABELS, money } from '../finance/labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Services and prices (docs/plans/phase1-finance-catalogue.md §6.2): the one
// catalogue of what the facility charges for, with the price in a chosen list.

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const ALL = 'all';
const categoryFilterOptions = [
  { id: ALL, label: 'All categories' },
  ...SERVICE_ITEM_CATEGORIES.map((c) => ({ id: c, label: CATEGORY_LABELS[c] })),
];

function AddItem({
  onDone,
  priceListId,
  priceListName,
  canPrice,
}: {
  onDone: (itemId: string) => void;
  priceListId: string | null;
  priceListName: string | null;
  canPrice: boolean;
}) {
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ServiceItemKind>('service');
  const [category, setCategory] = useState<ServiceItemCategory | null>(null);
  const [billingUnit, setBillingUnit] = useState('each');
  const [cents, setCents] = useState<bigint | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const categories = SERVICE_ITEM_CATEGORIES.filter((c) => categoryMatchesKind(c, kind)).map(
    (c) => ({ id: c, label: CATEGORY_LABELS[c] }),
  );
  const add = useMutation({
    mutationFn: () =>
      createItem({
        clientRequestId,
        code: code.trim(),
        name: name.trim(),
        kind,
        category: category ?? 'other',
        billingUnit: billingUnit.trim(),
        initialPrices:
          priceListId !== null && cents !== null
            ? [{ priceListId, unitPrice: { currency: 'KES', cents: cents.toString() } }]
            : [],
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      toasts.show({ title: 'Service added', description: created.name });
      onDone(created.id);
    },
  });
  const valid =
    code.trim() !== '' &&
    name.trim().length >= 2 &&
    category !== null &&
    billingUnit.trim() !== '' &&
    priceError === null;
  return (
    <form
      className={styles.inlineForm}
      aria-label="Add service or item"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (valid) add.mutate();
      }}
    >
      <div className={styles.row}>
        <TextField
          label="Code"
          description="Your own code, unique in the catalogue, like LAB-014."
          value={code}
          onChange={setCode}
          {...(tried && code.trim() === '' ? { errorMessage: 'Enter a code.' } : {})}
        />
        <TextField
          label="Name"
          description="As it should read on a bill."
          value={name}
          onChange={setName}
          {...(tried && name.trim().length < 2 ? { errorMessage: 'Enter a name.' } : {})}
        />
      </div>
      <RadioGroup
        label="Kind"
        value={kind}
        options={SERVICE_ITEM_KINDS.map((k) => ({
          id: k,
          label: KIND_LABELS[k],
          description:
            k === 'service' ? 'Something staff do.' : 'Something kept in stock and dispensed.',
        }))}
        onChange={(value) => {
          setKind(value as ServiceItemKind);
          setCategory(null);
        }}
      />
      <div className={styles.row}>
        <Select
          label="Category"
          placeholder="Choose a category"
          options={categories}
          value={category}
          onChange={(value) => {
            setCategory(value as ServiceItemCategory);
          }}
          {...(tried && category === null ? { errorMessage: 'Choose a category.' } : {})}
        />
        <TextField
          label="Billing unit"
          description="What one price covers: each, tablet, bottle, day."
          value={billingUnit}
          onChange={setBillingUnit}
          {...(tried && billingUnit.trim() === '' ? { errorMessage: 'Enter a unit.' } : {})}
        />
      </div>
      {canPrice && priceListId !== null ? (
        <MoneyField
          label={`${priceListName ?? 'Price'} price`}
          description="Optional. Starts now. Leave empty to price it later."
          value={cents}
          onChange={(value, error) => {
            setCents(value);
            setPriceError(error);
          }}
        />
      ) : null}
      {add.error === null ? null : (
        <Notice tone="error">{message(add.error, "It wasn't added. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={add.isPending} pendingLabel="Adding…">
          Add service
        </Button>
        <Button
          variant="quiet"
          onPress={() => {
            onDone('');
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function ServicesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'catalogue.read');
  const online = useOnline();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search.trim());
  const [category, setCategory] = useState<string>(ALL);
  const [chosenList, setChosenList] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const lists = useQuery({ ...priceListsQuery, enabled: allowed });
  const activeLists = (lists.data ?? []).filter((l) => l.status === 'active');
  const priceList =
    activeLists.find((l) => l.id === chosenList) ??
    activeLists.find((l) => l.name === 'Cash') ??
    activeLists[0] ??
    null;

  const filter = {
    ...(deferredSearch === '' ? {} : { q: deferredSearch }),
    ...(category === ALL ? {} : { category: category as ServiceItemCategory }),
    ...(priceList === null ? {} : { priceListId: priceList.id }),
  };
  const items = useInfiniteQuery({
    queryKey: ['catalogue', 'items', filter],
    queryFn: ({ pageParam }) =>
      listItems(pageParam === null ? filter : { ...filter, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: allowed && !lists.isPending,
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title="Services and prices" />
        <Notice tone="info">
          You don't have permission to see the service catalogue. Ask an administrator if you need
          it.
        </Notice>
      </>
    );
  }

  const canManage = can(session, 'catalogue.manage');
  const rows = items.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <SettingsPage
      title="Services and prices"
      panel
      {...(canManage && !adding
        ? {
            actions: (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add service or item
              </Button>
            ),
          }
        : {})}
    >
      {online ? null : (
        <Notice tone="info">Changing the catalogue needs a connection to Wodi.</Notice>
      )}
      {adding ? (
        <AddItem
          priceListId={priceList?.id ?? null}
          priceListName={priceList?.name ?? null}
          canPrice={can(session, 'prices.manage')}
          onDone={(itemId) => {
            setAdding(false);
            if (itemId !== '')
              void navigate({ to: '/settings/services/$itemId', params: { itemId } });
          }}
        />
      ) : null}
      <div className={styles.row}>
        <TextField
          label="Find a service or item"
          description="Type part of the name or the code."
          value={search}
          onChange={setSearch}
        />
        <Select
          label="Category"
          options={categoryFilterOptions}
          value={category}
          onChange={(value) => {
            setCategory(String(value));
          }}
        />
        {activeLists.length > 1 ? (
          <Select
            label="Show prices from"
            options={activeLists.map((l) => ({ id: l.id, label: l.name }))}
            value={priceList?.id ?? null}
            onChange={(value) => {
              setChosenList(String(value));
            }}
          />
        ) : null}
      </div>
      {priceList !== null && can(session, 'prices.manage') && can(session, 'imports.read') ? (
        <p>
          <Link
            to="/settings/imports"
            search={{ kind: 'price_list', priceListId: priceList.id }}
          >{`Import prices into ${priceList.name}`}</Link>
        </p>
      ) : null}
      {items.isPending ? <LoadingBlock label="Loading the catalogue" rows={5} /> : null}
      {items.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void items.refetch()}>
              Try again
            </Button>
          }
        >
          {message(items.error, "The catalogue couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {items.isSuccess ? (
        <Table flat aria-label="Services and items">
          <TableHeader>
            <Column>Code</Column>
            <Column isRowHeader>Name</Column>
            <Column>Category</Column>
            <Column numeric>{priceList === null ? 'Price' : `${priceList.name} price`}</Column>
            <Column>Status</Column>
          </TableHeader>
          <TableBody
            items={rows}
            renderEmptyState={() =>
              deferredSearch === '' && category === ALL
                ? 'No services yet. Add a service or item to start the catalogue.'
                : 'Nothing matches. Check the spelling, or search by code.'
            }
          >
            {(item) => (
              <Row id={item.id}>
                <Cell>
                  <span className="wodi-id">{item.code}</span>
                </Cell>
                <Cell>
                  <Link to="/settings/services/$itemId" params={{ itemId: item.id }}>
                    {item.name}
                  </Link>
                </Cell>
                <Cell>{CATEGORY_LABELS[item.category]}</Cell>
                <Cell numeric>
                  <span className="wodi-num">{money(item.price)}</span>
                </Cell>
                <Cell>
                  <Tag state={item.status === 'active' ? 'active' : 'neutral'}>
                    {item.status === 'active' ? 'In use' : 'Not in use'}
                  </Tag>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      {items.hasNextPage ? (
        <div className={styles.actions}>
          <Button
            isPending={items.isFetchingNextPage}
            pendingLabel="Loading…"
            onPress={() => void items.fetchNextPage()}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </SettingsPage>
  );
}
